import { InitPageStrict } from "../index";
import { showToast } from "../../utils/toast";

type ToastKind = "success" | "error";

interface PageConfig {
  paths: { stores: string; clients: string; bulkUpdate: string; update: string };
  toast: { message: string; kind: ToastKind } | null;
  t: Record<string, string>;
}

interface LinkLocation {
  code: string;
  name?: string;
  default?: boolean;
  locked?: boolean;
}

const TOAST_STORAGE_KEY = "mch-toast";
const FLEET_DETAIL_KEY = "mchFleetDetail";

const KEBAB_ICONS = {
  expand: '<path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/>',
  pencil: '<path d="M16.4 3.6 20.4 7.6 8 20H4v-4z"/><path d="M14.4 5.6 18.4 9.6"/>',
  link:
    '<path d="M10.4 13.6a4 4 0 0 0 5.7 0l2.8-2.8a4 4 0 0 0-5.7-5.7l-1.6 1.6"/>' +
    '<path d="M13.6 10.4a4 4 0 0 0-5.7 0l-2.8 2.8a4 4 0 0 0 5.7 5.7l1.6-1.6"/>',
  note: '<path d="M5 3.6h14v16.8H5z"/><path d="M8.4 8.4h7.2M8.4 12h7.2M8.4 15.6h4.4"/>',
  trash: '<path d="M4.4 6.8h15.2"/><path d="M9.2 6.8V3.8h5.6v3"/><path d="M6.4 6.8 7.4 20.2h9.2l1-13.4"/>',
};

declare global {
  interface Window {
    mchToast: (message: string, kind: ToastKind) => void;
    mchToastAfterReload: (message: string, kind: ToastKind) => void;
  }
}

class ManageCdmHandhelds {
  config: PageConfig;
  t: Record<string, string>;
  pendingStores: LinkLocation[] = [];
  pendingCode: string | null = null;
  pendingAlias: string | null = null;
  uploading = false;
  kebabId: string | null = null;
  $kmenu: JQuery;

  constructor(root: HTMLElement) {
    this.config = JSON.parse(root.dataset.mchConfig || "{}");
    this.t = this.config.t || {};
    this.$kmenu = $("#mch-kmenu");

    window.mchToast = showToast;
    window.mchToastAfterReload = (message, kind) => this.toastAfterReload(message, kind);

    this.replayToast();
    this.bindModals();
    this.bindDownloads();
    this.bindUpload();
    this.bindFilters();
    this.bindFleetDetail();
    this.bindRows();
    this.bindKebab();
    this.bindLinkDialog();
    this.initSelects();
    this.refreshSelection();
  }

  toastAfterReload(message: string, kind: ToastKind) {
    window.sessionStorage.setItem(TOAST_STORAGE_KEY, JSON.stringify({ message, kind }));
  }

  toastError(xhr: JQuery.jqXHR, fallback: string) {
    let message = fallback;
    try {
      const body = JSON.parse(xhr.responseText);
      message = body.message || (body.base && body.base[0]) || fallback;
    } catch (e) {}
    showToast(message, "error");
  }

  replayToast() {
    const parked = window.sessionStorage.getItem(TOAST_STORAGE_KEY);
    if (parked) {
      window.sessionStorage.removeItem(TOAST_STORAGE_KEY);
      try {
        const toast = JSON.parse(parked);
        showToast(toast.message, toast.kind);
      } catch (e) {}
    }

    if (this.config.toast) showToast(this.config.toast.message, this.config.toast.kind);
  }

  openModal(id: string) {
    $("#" + id).addClass("open");
  }

  closeModal(id: string) {
    $("#" + id).removeClass("open");
  }

  bindModals() {
    $(document).on("ajax:error", (_event, xhr) => {
      this.toastError(xhr, this.t.saveFailed);
    });

    $(document).on("click", (event) => {
      const $backdrop = $(event.target).closest(".modal-backdrop");

      if ($backdrop.length && (event.target as unknown) === $backdrop[0]) {
        $backdrop.removeClass("open");
        return;
      }

      const $close = $(event.target).closest("[data-close]");
      if ($close.length) $close.closest(".modal-backdrop").removeClass("open");
    });

    $(document).on("keydown", (event) => {
      if (event.key === "Escape" || event.keyCode === 27) $(".modal-backdrop.open").removeClass("open");
    });

    $("#btnExport").on("click", () => this.openModal("export-modal"));
    $("#btnSend").on("click", () => this.openModal("upload-modal"));
    $("#exportBtn").on("click", () => this.closeModal("export-modal"));
  }

  bindDownloads() {
    $(document).on("click", '[data-behavior="mch-download"]', (event) => {
      event.preventDefault();
      const $source = $(event.currentTarget);
      const url = String($source.data("url"));
      const name = decodeURIComponent(url.split("?")[0].split("/").pop() || "") || $source.data("build") + ".apk";

      $("#mch-download-scope").text(this.t.downloadScope + " " + $source.data("build") + ".");
      $("#mch-download-filename").text(name);
      $("#mch-download-confirm").attr("href", url).attr("download", name);
      this.openModal("download-modal");
    });

    $(document).on("click", '[data-behavior="mch-edit-min-version"]', (event) => {
      const $source = $(event.currentTarget);
      $("#min-version-model").val($source.data("model"));
      $("#min-version-input").val($source.data("min-version"));
      $("#mch-min-version-build").text($source.data("build"));
      this.openModal("min-version-modal");
    });

    $("#mch-download-confirm").on("click", () => this.closeModal("download-modal"));
  }

  setProgress(percent: number, label?: string) {
    $("#apkProgressFill").css("width", percent + "%");
    $("#apkProgressPct").text(Math.round(percent) + "%");
    if (label) $("#apkProgressLabel").text(label);
  }

  endUpload() {
    this.uploading = false;
    $("#apkProgress").removeClass("visible");
    $("#btnSend").prop("disabled", false);
    this.setProgress(0, this.t.uploading);
  }

  bindUpload() {
    const $dropzone = $("#file-dropzone");
    const fileInput = () => $("#handheld_apk_handheld_apk_file")[0] as HTMLInputElement;

    $dropzone.on("click", (event) => {
      if ($(event.target).closest('input[type="file"]').length) return;
      event.preventDefault();
      $(fileInput()).trigger("click");
    });

    $dropzone.on("dragover dragenter", (event) => {
      event.preventDefault();
      event.stopPropagation();
      $dropzone.addClass("dragover");
    });

    $dropzone.on("dragleave dragend drop", (event) => {
      event.preventDefault();
      event.stopPropagation();
      $dropzone.removeClass("dragover");
    });

    $dropzone.on("drop", (event) => {
      const transferData = (event.originalEvent as DragEvent).dataTransfer;
      const files = transferData && transferData.files;
      if (!files || !files.length) return;

      const file = files[0];
      if (!/\.apk$/i.test(file.name)) {
        showToast(this.t.dropApk, "error");
        return;
      }

      const input = fileInput();
      if (window.DataTransfer && input) {
        const transfer = new DataTransfer();
        transfer.items.add(file);
        input.files = transfer.files;
      }
      $(input).trigger("change");
    });

    $(document).on("dragover drop", (event) => {
      if ($(event.target).closest("#file-dropzone").length) return;
      event.preventDefault();
    });

    $("#handheld_apk_handheld_apk_file").on("change", (event) => {
      const input = event.currentTarget as HTMLInputElement;
      const file = input.files && input.files[0];
      $("#apk-file-name").text(file ? file.name : this.t.apkFile);
    });

    $(window).on("beforeunload", () => {
      if (this.uploading) return this.t.uploadLeave;
    });

    $("#upload-apk-form").on("submit", (event) => {
      event.preventDefault();
      if (this.uploading) return;

      const form = event.currentTarget as HTMLFormElement;
      const input = fileInput();
      if (!input.files || !input.files.length) {
        showToast(this.t.chooseApk, "error");
        return;
      }

      this.uploading = true;
      this.closeModal("upload-modal");
      $("#btnSend").prop("disabled", true);
      this.setProgress(0, this.t.uploading);
      $("#apkProgress").addClass("visible");

      $.ajax({
        url: form.action,
        method: "POST",
        data: new FormData(form),
        dataType: "json",
        processData: false,
        contentType: false,
        xhr: () => {
          const xhr = $.ajaxSettings.xhr();
          xhr.upload.addEventListener("progress", (progress) => {
            if (!progress.lengthComputable) return;

            const percent = (progress.loaded / progress.total) * 100;
            this.setProgress(Math.min(percent, 99));
            if (percent >= 100) this.setProgress(99, this.t.saving);
          });
          return xhr;
        },
        success: (response) => {
          this.setProgress(100, this.t.done);
          this.uploading = false;
          this.toastAfterReload((response && response.message) || this.t.apkUploaded, "success");
          setTimeout(() => window.location.reload(), 600);
        },
        error: (xhr) => {
          this.endUpload();
          this.toastError(xhr, this.t.apkUploadFailed);
        },
      });
    });
  }

  bindFilters() {
    $(".mch-filter-select").on("change", (event) => {
      window.location.href = (event.currentTarget as HTMLSelectElement).value;
    });
  }

  bindFleetDetail() {
    const fleetDetail = document.getElementById("mch-fleet-detail");
    const fleetLabel = document.getElementById("mch-fleet-toggle-label");
    if (!fleetDetail) return;

    const paint = (open: boolean) => {
      $(fleetDetail).toggle(open);
      if (fleetLabel) fleetLabel.textContent = open ? this.t.less : this.t.detail;
      $("#mch-fleet-toggle .fa").toggleClass("fa-chevron-down", !open).toggleClass("fa-chevron-up", open);
    };

    let initial = false;
    try {
      initial = window.localStorage.getItem(FLEET_DETAIL_KEY) === "1";
    } catch (error) {}
    paint(initial);

    $("#mch-fleet-toggle").on("click", () => {
      const open = !$(fleetDetail).is(":visible");
      try {
        window.localStorage.setItem(FLEET_DETAIL_KEY, open ? "1" : "0");
      } catch (error) {}
      paint(open);
    });
  }

  toggleRow(id: string | number) {
    const $panel = $("#mch-tel-" + id);
    if (!$panel.length) return false;

    const open = !$panel.is(":visible");
    $panel.toggle(open);
    $('.mch-row[data-id="' + id + '"]').toggleClass("sel", open);
    return open;
  }

  selectedIds(): string[] {
    return $(".mch-checkbox:checked")
      .map((_index, box) => $(box).val() as string)
      .get();
  }

  refreshSelection() {
    const bulk = this.selectedIds().length > 1;
    $("#mch-link-bulk-save").toggleClass("disabled", !bulk).prop("disabled", !bulk);
  }

  bindRows() {
    $(".sn-table").on("click", ".mch-row", (event) => {
      if ($(event.target).closest("a, button, input, label, select").length) return;
      this.toggleRow($(event.currentTarget).data("id"));
    });

    $("#mch-select-all").on("change", (event) => {
      $(".mch-checkbox").prop("checked", $(event.currentTarget).prop("checked"));
      this.refreshSelection();
    });

    $(".sn-table").on("change", ".mch-checkbox", () => {
      const boxes = $(".mch-checkbox");
      $("#mch-select-all").prop("checked", boxes.length === boxes.filter(":checked").length);
      this.refreshSelection();
    });
  }

  kebabItem(icon: string, label: string, options: { danger?: boolean; href?: string; remote?: boolean; act?: string }) {
    const $item = $("<a/>", { class: options.danger ? "dang" : "", href: options.href || "javascript:void(0)" });
    $item.append(
      '<svg class="ic" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
        'stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">' +
        KEBAB_ICONS[icon] +
        "</svg>"
    );
    $item.append(document.createTextNode(label));
    if (options.remote) $item.attr("data-remote", "true");
    if (options.act) $item.attr("data-act", options.act);
    return $item;
  }

  closeKebab() {
    this.kebabId = null;
    this.$kmenu.attr("hidden", "hidden").empty();
  }

  openDeleteDialog($source: JQuery) {
    const tenant = $source.data("tenant");
    const name = $source.data("name");
    $("#mch-delete-title").text(
      tenant
        ? this.t.deleteThe + " " + name + ", " + this.t.assignedTo + " " + tenant + "?"
        : this.t.deleteThe + " " + name + "?"
    );
    $("#mch-delete-confirm").attr("href", $source.data("delete-url"));
    this.openModal("delete-modal");
  }

  openLinkDialog($source: JQuery) {
    this.openDialog($source.data("id"), {
      clientCode: $source.data("client-code"),
      clientName: $source.data("client-name"),
      aliasName: $source.data("alias-name"),
      locations: $source.data("locations") || [],
    });
  }

  bindKebab() {
    $(document).on("click", '[data-behavior="mch-kebab"]', (event) => {
      event.preventDefault();
      event.stopPropagation();

      const button = event.currentTarget as HTMLElement;
      const $button = $(button);
      const id = String($button.data("id"));
      if (this.kebabId === id) {
        this.closeKebab();
        return;
      }

      this.kebabId = id;
      const open = $("#mch-tel-" + id).is(":visible");

      this.$kmenu
        .empty()
        .append(
          $('<div class="kh"/>')
            .append($("<b/>").text($button.data("name")))
            .append($("<span/>").text($button.data("tenant") || this.t.notLinked))
        )
        .append(this.kebabItem("expand", open ? this.t.hideSnapshot : this.t.showSnapshot, { act: "open" }))
        .append(this.kebabItem("pencil", this.t.editInfo, { href: $button.data("edit-url"), remote: true }))
        .append(this.kebabItem("link", this.t.linkTenant, { act: "link" }))
        .append($('<div class="ksep"/>'))
        .append(this.kebabItem("note", this.t.note, { href: $button.data("note-url"), remote: true }))
        .append($('<div class="ksep"/>'))
        .append(this.kebabItem("trash", this.t.deleteHandheld, { act: "delete", danger: true }))
        .removeAttr("hidden");

      const rect = button.getBoundingClientRect();
      const width = this.$kmenu.outerWidth();
      const height = this.$kmenu.outerHeight();
      this.$kmenu.css({
        left: Math.max(10, rect.right - width) + "px",
        top:
          (rect.bottom + height + 12 > window.innerHeight
            ? Math.max(10, rect.top - height - 6)
            : rect.bottom + 5) + "px",
      });
    });

    this.$kmenu.on("click", "a", (event) => {
      const $button = $('[data-behavior="mch-kebab"][data-id="' + this.kebabId + '"]');
      const action = $(event.currentTarget).data("act");

      if (!action) {
        window.setTimeout(() => this.closeKebab(), 0);
        return;
      }

      this.closeKebab();
      if (action === "open") this.toggleRow($button.data("id"));
      if (action === "link") this.openLinkDialog($button);
      if (action === "delete") this.openDeleteDialog($button);
    });

    $(document).on("click", (event) => {
      if (this.kebabId && !$(event.target).closest("#mch-kmenu").length) this.closeKebab();
    });

    $(window).on("resize scroll", () => {
      if (this.kebabId) this.closeKebab();
    });
  }

  openDialog(id: string | number, data: { clientCode?: string; clientName?: string; aliasName?: string; locations?: LinkLocation[] }) {
    const ids = this.selectedIds();
    $("#link-modal").data("id", id);
    $("#mch-link-scope").text(ids.length > 1 ? ids.length + " " + this.t.bulkScope : this.t.editingHh + id);
    this.refreshSelection();

    this.pendingAlias = data.aliasName || data.clientName || "";
    this.pendingStores = data.locations || [];
    this.pendingCode = data.clientCode || null;

    this.resetLocations();
    this.setTenant(data.clientCode, data.clientName);
    this.openModal("link-modal");
  }

  setTenant(code?: string, name?: string) {
    const $select = $("#mch_client_id");
    $select.empty();
    if (code) $select.append(new Option(name || code, code, true, true));
    $select.trigger("change");
  }

  resetLocations() {
    $("#mch-location-list").empty();
    $("#mch-all-locations").prop("checked", false);
    this.renderEmptyRow();
  }

  renderEmptyRow() {
    if ($("#mch-location-list tr.mch-loc-row").length === 0) {
      $("#mch-location-list").append(
        $('<tr class="mch-empty-row"/>').append($('<td colspan="5"/>').text(this.t.noLocation))
      );
    }
  }

  addLocation(code: string, name?: string, flags: { default?: boolean; locked?: boolean } = {}) {
    if (!code || $('#mch-location-list tr[data-code="' + code + '"]').length) return;
    $("#mch-location-list .mch-empty-row").remove();
    $("#mch-location-list").append(
      $('<tr class="mch-loc-row"/>')
        .attr("data-code", code)
        .append($("<td/>").append($("<code/>").text(code)))
        .append(
          $("<td/>")
            .append($('<span class="mch-loc-name"/>').text(name || ""))
            .append($('<span class="mch-flag mch-flag-default"/>').text(this.t.default).hide())
            .append($('<span class="mch-flag mch-flag-locked"/>').text(this.t.locked).hide())
        )
        .append(
          $('<td class="ct"/>').append(
            $('<input type="checkbox" class="mch-default-cb"/>').prop("checked", !!flags.default)
          )
        )
        .append(
          $('<td class="ct"/>').append(
            $('<input type="checkbox" class="mch-locked-cb"/>').prop("checked", !!flags.default && !!flags.locked)
          )
        )
        .append(
          $('<td class="rt"/>').append(
            $('<button class="mini red mch-remove-location" type="button"/>').text(this.t.remove)
          )
        )
    );
    this.syncLocationFlags();
  }

  syncLocationFlags() {
    $("#mch-location-list tr.mch-loc-row").each((_index, row) => {
      const $row = $(row);
      const isDefault = $row.find(".mch-default-cb").is(":checked");
      const $locked = $row.find(".mch-locked-cb");
      if (!isDefault) $locked.prop("checked", false);
      $locked.prop("disabled", !isDefault);
      $row.find(".mch-flag-default").toggle(isDefault);
      $row.find(".mch-flag-locked").toggle(isDefault && $locked.is(":checked"));
    });
  }

  selectDefaultLocation(row: HTMLElement) {
    const $others = $("#mch-location-list tr.mch-loc-row").not(row);
    const carryLocked = $others.find(".mch-default-cb:checked").closest("tr").find(".mch-locked-cb").is(":checked");
    $others.find(".mch-default-cb, .mch-locked-cb").prop("checked", false);
    if (carryLocked) $(row).find(".mch-locked-cb").prop("checked", true);
  }

  defaultLocation(): LinkLocation | null {
    const $row = $("#mch-location-list .mch-default-cb:checked").first().closest("tr");
    if (!$row.length) return null;

    return { code: $row.data("code").toString(), locked: $row.find(".mch-locked-cb").is(":checked") };
  }

  formatStoreLabel(data) {
    if (!data || !data.id) return data && data.text ? data.text : "";

    const $option = $(data.element);
    const code = $option.data("storeCode") || data.id || "";
    const name = $option.data("storeName") || data.text || "";

    return code && name ? code + " - " + name : data.text;
  }

  storeMatcher(params, data) {
    if ($.trim(params.term) === "") return data;
    if (typeof data.element === "undefined") return null;

    const term = params.term.toLowerCase();
    const $option = $(data.element);
    const haystack = [
      data.text || "",
      $option.data("storeName") || "",
      $option.data("storeCode") || data.id || "",
      data.id || "",
    ];

    return haystack.some((value) => value.toString().toLowerCase().indexOf(term) > -1) ? data : null;
  }

  selectedLocationCodes(): string[] {
    return $("#mch-location-list tr.mch-loc-row")
      .map((_index, row) => $(row).data("code").toString())
      .get();
  }

  linkPayload() {
    return {
      client_code: $("#mch_client_id").val() as string,
      alias_name: $("#mch_alias_name").val() as string,
      store_codes: this.selectedLocationCodes(),
      default_store_code: this.defaultLocation()?.code || "",
      default_locked: !!this.defaultLocation()?.locked,
      cdm_ids: undefined as string[] | undefined,
    };
  }

  validLink(payload: { client_code: string; store_codes: string[] }) {
    if (!payload.client_code) {
      showToast(this.t.selectTenant, "error");
      return false;
    }
    if (!payload.store_codes.length) {
      showToast(this.t.noStore, "error");
      return false;
    }
    return true;
  }

  linkFailed(xhr: JQuery.jqXHR) {
    this.closeModal("link-modal");
    this.toastError(xhr, this.t.linkFailed);
  }

  bindLinkDialog() {
    $("#mch-location-list").on("click", ".mch-remove-location", (event) => {
      event.preventDefault();
      $(event.currentTarget).closest("tr").remove();
      this.renderEmptyRow();
    });

    $("#mch-location-list").on("change", ".mch-default-cb", (event) => {
      const checkbox = event.currentTarget as HTMLInputElement;
      if (checkbox.checked) this.selectDefaultLocation($(checkbox).closest("tr")[0]);
      this.syncLocationFlags();
    });

    $("#mch-location-list").on("change", ".mch-locked-cb", () => this.syncLocationFlags());

    $("#mch-add-location").on("click", () => {
      const $option = $("#mch_store_id option:selected");
      this.addLocation($option.val() as string, $option.data("store-name"));
    });

    $("#mch-all-locations").on("change", (event) => {
      const current = this.defaultLocation();
      $("#mch-location-list").empty();
      if ($(event.currentTarget).is(":checked")) {
        $("#mch_store_id option").each((_index, option) => {
          const code = $(option).val() as string;
          const isDefault = !!current && current.code === code;
          this.addLocation(code, $(option).data("store-name"), { default: isDefault, locked: isDefault && !!current?.locked });
        });
      }
      this.renderEmptyRow();
    });

    $("#mch_client_id").on("change", (event) => {
      const code = $(event.currentTarget).val() as string;

      if (this.pendingAlias !== null) {
        $("#mch_alias_name").val(this.pendingAlias);
        this.pendingAlias = null;
      } else {
        $("#mch_alias_name").val($("#mch_client_id option:selected").text());
      }

      $("#mch_store_id").empty().trigger("change");
      this.resetLocations();
      if (!code) return;

      $.getJSON(this.config.paths.stores, { client_code: code }, (result) => {
        const $stores = $("#mch_store_id");
        $stores.append('<option value=""></option>');
        (result.stores || []).forEach((store) => {
          $stores.append(
            $("<option></option>")
              .val(store[1])
              .text(store[1] + " - " + store[0])
              .attr("data-store-name", store[0])
              .attr("data-store-code", store[1])
          );
        });
        $stores.val(null).trigger("change");

        if (this.pendingStores.length && this.pendingCode === code) {
          $("#mch-location-list").empty();
          this.pendingStores.forEach((location) => {
            const $option = $stores.find('option[value="' + location.code + '"]');
            this.addLocation(location.code, $option.data("store-name"), location);
          });
          this.renderEmptyRow();
          this.pendingStores = [];
        }
      }).fail(() => {
        this.closeModal("link-modal");
        showToast(this.t.locationsFailed, "error");
      });
    });

    $("#mch-link-save").on("click", (event) => {
      const $btn = $(event.currentTarget);
      const payload = this.linkPayload();
      if ($btn.prop("disabled") || !this.validLink(payload)) return;
      $btn.prop("disabled", true);

      $.ajax({
        method: "PUT",
        url: this.config.paths.update + "/" + $("#link-modal").data("id") + ".json",
        data: { public_cdm_handheld: payload },
        success: () => {
          this.toastAfterReload(this.t.handheldUpdated, "success");
          window.location.reload();
        },
        error: (xhr) => {
          $btn.prop("disabled", false);
          this.linkFailed(xhr);
        },
      });
    });

    $("#mch-link-bulk-save").on("click", (event) => {
      const $btn = $(event.currentTarget);
      if ($btn.hasClass("disabled") || $btn.prop("disabled")) return;

      const payload = this.linkPayload();
      if (!this.validLink(payload)) return;
      payload.cdm_ids = this.selectedIds();
      $btn.prop("disabled", true);

      $.ajax({
        method: "POST",
        url: this.config.paths.bulkUpdate,
        data: { public_cdm_handheld: payload },
        success: (response) => {
          const updated = response && response.updated;
          const failed = (response && response.failed_cdms) || [];

          if (failed.length) {
            this.toastAfterReload(failed.length + " " + this.t.someFailed, "error");
          } else {
            this.toastAfterReload(updated ? updated + " " + this.t.nUpdated : this.t.allUpdated, "success");
          }
          window.location.reload();
        },
        error: (xhr) => {
          this.refreshSelection();
          this.linkFailed(xhr);
        },
      });
    });
  }

  initSelects() {
    const select2 = ($.fn as any).select2;
    if (!select2) return;

    ($("#mch_client_id") as any).select2({
      placeholder: this.t.tenantPlaceholder,
      allowClear: true,
      width: "100%",
      ajax: { url: this.config.paths.clients },
    });

    ($("#mch_store_id") as any).select2({
      placeholder: this.t.locationPlaceholder,
      allowClear: true,
      width: "100%",
      matcher: (params, data) => this.storeMatcher(params, data),
      templateResult: (data) => this.formatStoreLabel(data),
      templateSelection: (data) => this.formatStoreLabel(data),
    });
  }
}

export function LoadPage() {
  InitPageStrict("manage_cdm_handhelds index", () => {
    const root = document.getElementById("mch-page");
    if (root) new ManageCdmHandhelds(root);
  });
}
